// src/components/recharge/ResetTool.jsx
//
// Full-screen runner for a Reset & Recharge tool we own (data/resetTools.js):
// a breath pacer (animated circle, variable phase lengths) or a stepped guide
// (cue + detail + countdown). Finishing logs a session to the member's
// private journal and ticks the Guided Path recovery checkpoint.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, SkipForward, X, Check, RotateCcw } from 'lucide-react';
import { toolDurationSecs } from '../../data/resetTools';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

const useReducedMotion = () => {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduced(mq.matches);
        const onChange = (e) => setReduced(e.matches);
        if (mq.addEventListener) { mq.addEventListener('change', onChange); return () => mq.removeEventListener('change', onChange); }
        mq.addListener(onChange); return () => mq.removeListener(onChange);
    }, []);
    return reduced;
};

const ResetTool = ({ tool, energyBefore = null, onFinish, onClose }) => {
    const reducedMotion = useReducedMotion();
    const total = useMemo(() => toolDurationSecs(tool), [tool]);
    // Flatten to a timeline of segments so breath + guided share one engine
    const segments = useMemo(() => {
        if (tool.kind === 'breath') {
            const out = [];
            for (let r = 0; r < (tool.rounds || 1); r++) tool.phases.forEach(p => out.push({ ...p, round: r + 1 }));
            return out;
        }
        return tool.steps.map(s => ({ label: s.cue, detail: s.detail, secs: s.secs, scale: 1 }));
    }, [tool]);

    const [running, setRunning] = useState(false);
    const [segIdx, setSegIdx] = useState(0);
    const [segLeft, setSegLeft] = useState(segments[0]?.secs || 0);
    const [elapsed, setElapsed] = useState(0);
    const [finished, setFinished] = useState(false);
    const [energyAfter, setEnergyAfter] = useState(null);
    const tickRef = useRef(null);

    // One-second engine
    useEffect(() => {
        if (!running || finished) return undefined;
        tickRef.current = setInterval(() => {
            setElapsed(e => e + 1);
            setSegLeft(left => {
                if (left > 1) return left - 1;
                // advance
                setSegIdx(i => {
                    const next = i + 1;
                    if (next >= segments.length) { setRunning(false); setFinished(true); return i; }
                    return next;
                });
                return 0;
            });
        }, 1000);
        return () => clearInterval(tickRef.current);
    }, [running, finished, segments.length]);

    // When the segment changes, load its length
    useEffect(() => { if (!finished) setSegLeft(segments[segIdx]?.secs || 0); }, [segIdx, segments, finished]);

    const seg = segments[segIdx] || segments[segments.length - 1];
    const isBreath = tool.kind === 'breath';
    const progress = Math.min(1, elapsed / Math.max(1, total));

    const skip = () => {
        if (finished) return;
        if (segIdx + 1 >= segments.length) { setRunning(false); setFinished(true); return; }
        setSegIdx(i => i + 1);
    };
    const restart = () => { setRunning(false); setFinished(false); setSegIdx(0); setElapsed(0); setSegLeft(segments[0]?.secs || 0); setEnergyAfter(null); };
    const finish = () => { if (onFinish) onFinish({ toolId: tool.id, minutes: Math.max(1, Math.round(elapsed / 60)), energyBefore, energyAfter, completed: true }); if (onClose) onClose(); };

    return (
        <div className="fixed inset-0 z-[210] bg-slate-950 text-white flex flex-col animate-in fade-in duration-200">
            {/* top bar */}
            <div className="shrink-0 flex items-center justify-between px-5 py-4">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/50">{tool.intent} · about {tool.minutes} min</p>
                    <h2 className="font-black text-lg leading-tight">{tool.title}</h2>
                </div>
                <button onClick={onClose} aria-label="Close" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X size={20} /></button>
            </div>
            {/* progress */}
            <div className="shrink-0 mx-5 h-1 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-white/70 rounded-full transition-all duration-1000 ease-linear" style={{ width: `${progress * 100}%` }} /></div>

            {/* body */}
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 text-center">
                {finished ? (
                    <div className="max-w-sm w-full animate-in zoom-in-95 duration-300">
                        <div className="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-4 shadow-lg"><Check size={30} strokeWidth={3} /></div>
                        <h3 className="text-2xl font-black">Done.</h3>
                        <p className="text-white/70 font-medium mt-1">{fmt(elapsed)} of {tool.title.toLowerCase()}.</p>
                        <p className="text-sm font-bold text-white/80 mt-6 mb-2">How much do you have now?</p>
                        <div className="grid grid-cols-5 gap-1.5">
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                                <button key={n} onClick={() => setEnergyAfter(n)} className={`h-11 rounded-xl font-black text-sm border transition-colors ${energyAfter === n ? 'bg-white text-slate-900 border-white' : 'bg-white/5 border-white/15 hover:bg-white/15'}`}>{n}</button>
                            ))}
                        </div>
                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-white/40 mt-1.5"><span>Empty</span><span>Full</span></div>
                        <div className="flex gap-2 mt-6">
                            <button onClick={restart} className="px-4 py-3 rounded-xl font-bold text-sm bg-white/10 hover:bg-white/20 flex items-center gap-1.5"><RotateCcw size={16} /> Again</button>
                            <button onClick={finish} className="flex-1 px-4 py-3 rounded-xl font-bold text-sm bg-white text-slate-900 hover:bg-slate-100 flex items-center justify-center gap-1.5"><Check size={16} strokeWidth={3} /> Mark done</button>
                        </div>
                    </div>
                ) : isBreath ? (
                    <>
                        <div className="h-64 w-64 flex items-center justify-center" aria-live="polite">
                            <div
                                className="w-36 h-36 rounded-full bg-gradient-to-br from-brand-primary to-brand-dark shadow-[0_0_60px_rgba(255,255,255,0.15)] flex items-center justify-center"
                                style={{
                                    transform: `scale(${running ? seg.scale : 1})`,
                                    transition: reducedMotion ? 'none' : `transform ${running && !seg.hold ? seg.secs : 0.3}s ease-in-out`
                                }}
                            >
                                <span className="text-2xl font-black tabular-nums">{running ? segLeft : ''}</span>
                            </div>
                        </div>
                        <p className="text-2xl font-black mt-2">{running ? seg.label : 'Ready when you are'}</p>
                        <p className="text-white/60 font-medium mt-1">{running ? `Round ${seg.round} of ${tool.rounds}` : tool.blurb}</p>
                    </>
                ) : (
                    <div className="max-w-md w-full">
                        <p className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-3">Step {segIdx + 1} of {segments.length}</p>
                        <div className="text-6xl font-black tabular-nums mb-4">{running ? fmt(segLeft) : fmt(seg.secs)}</div>
                        <h3 className="text-2xl md:text-3xl font-black leading-tight">{seg.label}</h3>
                        <p className="text-white/70 font-medium mt-3 text-base">{seg.detail}</p>
                        {!running && elapsed === 0 && <p className="text-white/50 text-sm mt-6">{tool.blurb}</p>}
                    </div>
                )}
            </div>

            {/* controls */}
            {!finished && (
                <div className="shrink-0 px-5 pb-6 pt-2 flex items-center justify-center gap-3">
                    <button onClick={() => setRunning(r => !r)} className="px-8 py-4 rounded-2xl font-black text-base bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2 shadow-lg">
                        {running ? <Pause size={20} /> : <Play size={20} />} {running ? 'Pause' : elapsed > 0 ? 'Resume' : 'Start'}
                    </button>
                    {!isBreath && (
                        <button onClick={skip} className="px-4 py-4 rounded-2xl font-bold text-sm bg-white/10 hover:bg-white/20 flex items-center gap-1.5"><SkipForward size={18} /> Next</button>
                    )}
                    <span className="text-white/50 text-sm font-bold tabular-nums ml-2">{fmt(total - elapsed)}</span>
                </div>
            )}
        </div>
    );
};

export default ResetTool;
